import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  DollarCircleOutlined,
  FieldTimeOutlined,
  PlusCircleOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { FormInstance } from "antd";
import { SelectList } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { SelectOps } from "components/generalComponents/selectComponent/model/SelectOpsModel";
import { isEmpty, isObject } from "lodash";
import type { ComponentType, CSSProperties } from "react";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { SimTypeEnum } from "utils/enums/SimTypeEnum";
import {
  TransactionStatusEnum,
  TransactionTypeEnum,
} from "utils/enums/TransactionEnum";
import { avoidDuplicate, getMessage } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";

export class DataFormat {
  private static statusIcon = (
    Icon: ComponentType<{ style?: CSSProperties }>,
    color: string
  ) => <Icon style={{ color }} />;

  static platformOptions = (localizedStrings: typeof strings) => {
    return [
      { label: localizedStrings.GlobalLabels.All, value: "" },
      { label: "Web", value: "web" },
      { label: "App", value: "app" },
      { label: "GoGoSIM Web", value: "gogosim_web" },
    ];
  };

  static transactionStatusOptions(localStrings: typeof strings) {
    return [
      { label: localStrings.GlobalLabels.All, value: "" },
      {
        label: localStrings.AppTransactionHistory.Status.Success,
        value: TransactionStatusEnum.Success,
        color: "green",
      },
      {
        label: localStrings.AppTransactionHistory.Status.Failed,
        value: TransactionStatusEnum.Failed,
        color: "red",
      },
      {
        label: localStrings.AppTransactionHistory.Status.Process,
        value: TransactionStatusEnum.Proccess,
        color: "purple",
      },
      {
        label: localStrings.AppTransactionHistory.Status.Deleted,
        value: TransactionStatusEnum.Refund,
        color: "orange",
      },
      {
        label: localStrings.AppTransactionHistory.Status.Init,
        value: TransactionStatusEnum.Init,
        color: "blue",
      },
    ] as SelectList[];
  }

  static transactionTypeOptions(localStrings: typeof strings) {
    return [
      { label: localStrings.GlobalLabels.All, value: "" },
      {
        label: localStrings.AppTransactionHistory.Type.Normal,
        value: TransactionTypeEnum.Normal,
      },
      {
        label: localStrings.AppTransactionHistory.Type.User,
        value: TransactionTypeEnum.User,
      },
      {
        label: localStrings.AppTransactionHistory.Type.Partner,
        value: TransactionTypeEnum.Partner,
      },
    ] as SelectList[];
  }

  static b2cStatusOption = (localizedStrings: typeof strings) => {
    return [
      { label: localizedStrings.GlobalLabels.All, value: "" },
      {
        label: localizedStrings.AppTransactionHistory.Status.Init,
        value: TransactionStatusEnum.Init,
        color: "black",
        icon: DataFormat.statusIcon(PlusCircleOutlined, "black"),
      },
      {
        label: localizedStrings.AppTransactionHistory.Status.Process,
        value: TransactionStatusEnum.Proccess,
        color: "purple",
        icon: DataFormat.statusIcon(SyncOutlined, "purple"),
      },
      {
        label: localizedStrings.AppTransactionHistory.Status.Success,
        value: TransactionStatusEnum.Success,
        color: "green",
        icon: DataFormat.statusIcon(CheckCircleOutlined, "green"),
      },
      {
        label: localizedStrings.AppTransactionHistory.Status.Failed,
        value: TransactionStatusEnum.Failed,
        color: "red",
        icon: DataFormat.statusIcon(CloseCircleOutlined, "red"),
      },
      {
        label: localizedStrings.AppTransactionHistory.Status.Pending,
        value: TransactionStatusEnum.Pending,
        color: "orange",
        icon: DataFormat.statusIcon(ClockCircleOutlined, "orange"),
      },
      {
        label: localizedStrings.AppTransactionHistory.Status.PaymentSuccess,
        value: TransactionStatusEnum.PaymentSuccess,
        color: "blue",
        icon: DataFormat.statusIcon(DollarCircleOutlined, "blue"),
      },
      {
        label: localizedStrings.AppTransactionHistory.Status.Timeout,
        value: TransactionStatusEnum.Timeout,
        color: "orange",
        icon: DataFormat.statusIcon(FieldTimeOutlined, "orange"),
      },
      {
        label: localizedStrings.AppTransactionHistory.Status.Deleted,
        value: TransactionStatusEnum.Deleted,
        color: "red",
        icon: DataFormat.statusIcon(DeleteOutlined, "red"),
      },
    ] as Array<SelectList>;
  };

  static simTypeOptions(localStrings: typeof strings) {
    return [
      { label: localStrings.GlobalLabels.All, value: "" },
      {
        label: localStrings.SimManagement.SimType.Physical,
        value: SimTypeEnum.physical,
      },
      {
        label: localStrings.SimManagement.SimType.ESim,
        value: SimTypeEnum.eSim,
      },
    ] as SelectList[];
  }

  static simNumberTypeOptions(localStrings: typeof strings) {
    return [
      { label: localStrings.GlobalLabels.All, value: "" },
      ...Object.entries(
        localStrings.AppTransactionHistory.Labels.NumberTypeOps
      )?.map(([keys, vals]) => ({
        label: vals,
        value: keys,
      })),
    ];
  }

  static renderItem = (
    label: string,
    value: React.ReactNode,
    color = "black"
  ) => {
    return (
      <div className="flex gap-x-3 justify-between w-full my-2">
        <span className="font-[550] text-[#909090] min-w-[100px]">
          {label}:
        </span>
        <span
          style={{
            color: color,
            fontWeight:
              color !== "black"
                ? 500
                : label === strings.GlobalLabels.Status
                ? 550
                : "",
            textAlign: "right",
          }}
          className="overflow-visible text-ellipsis"
        >
          {value && value !== "-" && value !== "Invalid Date"
            ? value
            : strings.GlobalLabels.NoInfo}
        </span>
      </div>
    );
  };

  static removeUndefined(obj: Record<string, unknown>) {
    Object.keys(obj).forEach((key) => {
      const v = obj[key];
      if (
        v === undefined ||
        v === null ||
        (Array.isArray(v) && isEmpty(v)) ||
        (isObject(v) && isEmpty(v)) ||
        (typeof v === "string" && v.trim() === "")
      ) {
        delete obj[key];
      }
    });
    return obj;
  }

  static getToken() {
    return localStorage.getItem(CacheKeyEnum.isSavedForNextLogin) === "true"
      ? localStorage.getItem(CacheKeyEnum.mainToken)
      : sessionStorage.getItem(CacheKeyEnum.mainToken);
  }

  static clickToCopy = async (value: string) =>
    await window?.navigator?.clipboard
      ?.writeText(value)
      .then(() =>
        getMessage(
          `${strings.GlobalMessage.SavedToClipboard} - ${value}`,
          3,
          "success"
        )
      );

  static normalizeMultipleValue = (
    selected?: unknown[],
    isSingleItem: boolean = false
  ): unknown[] => {
    const values = Array.isArray(selected) ? selected : [];
    if (values.length === 0) {
      return [""];
    }

    if (values.includes("")) {
      if (values.at(-1) === "") {
        return [""];
      }

      const specificValues = values.filter((value) => value !== "");
      return isSingleItem ? specificValues.slice(-1) : specificValues;
    }

    return isSingleItem ? values.slice(-1) : values;
  };

  static handleMultipleChange = (
    selected: unknown[],
    filterKey: string,
    form: FormInstance,
    isSingleItem: boolean = false
  ) => {
    form.setFieldValue(
      filterKey,
      this.normalizeMultipleValue(selected, isSingleItem)
    );
  };

  static removeArrayTypeValue = <T extends Object>(obj: T) => {
    Object?.entries(obj)?.forEach(([keys, vals]) => {
      if (Array.isArray(vals)) {
        delete obj[keys];
      } else {
        return;
      }
    });
    return obj;
  };

  static includeArray = <T extends unknown>(
    firstArr: Array<T>,
    secondArr: Array<T>
  ) => {
    return !isEmpty(firstArr?.filter((child) => secondArr?.includes(child)))
      ? firstArr.filter(
          (child) => !isEmpty(child) && !secondArr?.includes(child)
        )
      : undefined;
  };

  static selectFilterOption = <T extends Object>(
    input: string,
    options: SelectOps & T,
    customKey?: Array<string>
  ) => {
    let hasLabel = options?.label?.toLowerCase().includes(input.toLowerCase());
    let hasValue = options?.value.toString() === input.toString().toLowerCase();
    let hasCustome = !!customKey && !isEmpty(customKey);
    if (
      hasLabel ||
      hasValue ||
      (hasCustome &&
        customKey?.some((item) => options[item]?.includes(input.toLowerCase())))
    ) {
      return true;
    }
    return false;
  };

  static autoCorrectJSON = (jsonString: string): string => {
    let correctedJSON = jsonString
      .replace(/(?<=\{|\,)\s*([\w]+)\s*:/g, '"$1":')
      .replace(/(?<=:)\s*'([^']*)'\s*(?=,|\}|$)/g, ' "$1"');
    correctedJSON = correctedJSON.replace(/,\s*([\]}])/g, "$1");
    return correctedJSON;
  };

  static toMbAndHour = <T extends number>(
    unit: string,
    baseValue: T,
    newValue: T
  ) => {
    if (unit === "mb" || unit === "gb") {
      let value = Math.round(((newValue as number) / 1000) * 200) / 200;
      return avoidDuplicate(baseValue, unit === "mb" ? value : newValue);
    } else {
      return unit === "hour" && newValue >= 1
        ? avoidDuplicate(baseValue, Math.round(newValue / 24))
        : newValue < 1 && unit === "hour"
        ? avoidDuplicate(baseValue, newValue)
        : newValue < 1 && unit === "day"
        ? avoidDuplicate(baseValue, Math.round(newValue * 24))
        : avoidDuplicate(baseValue, newValue);
    }
  };

  static prepareParamsForApi<T extends Object>(params: T): T {
    const copy = { ...params };
    DataFormat.removeArrayTypeValue(copy);
    return copy as unknown as T;
  }

  static serializeParams = (params: Record<string, any>): string => {
    const parts: string[] = [];
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (v === undefined || v === null) return;
          parts.push(
            `${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`
          );
        });
      } else {
        parts.push(
          `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
        );
      }
    });
    return parts.join("&");
  };

  static gbFormat = (mb: string) => {
    return (Number(mb) / 1024)?.toFixed(1);
  };
}
